import { IDeveloperField } from 'dromo-uploader-react'
import { OptionsMap } from '../typings'
import { createSelectField } from './createSelectField'

export const sharedFieldSettings = (
  options: OptionsMap,
  dateFormat: string,
): IDeveloperField[] => {
  const {
    salesTeams,
    salesFunnels,
    branches,
    leadSources,
    campaigns,
    statesProvinces,
    targetPests,
    locationTypes,
    divisions,
    contactMethods,
    contactTimes,
  } = options

  return [
    {
      key: 'createdDateTime',
      label: 'Created Date',
      type: [
        'datetime',
        {
          displayFormat: `${dateFormat}`,
          outputFormat: `${dateFormat} ZZ`,
        },
      ],
      validators: [{ validate: 'required' }],
    },
    {
      key: 'firstName',
      label: 'First Name',
      validators: [
        {
          validate: 'require_without_all',
          fields: ['lastName', 'companyName'],
          errorMessage: 'First Name OR Last Name OR Company Name is required',
        },
        {
          validate: 'length',
          min: 1,
          max: 15,
          errorMessage: 'First Name cannot exceed 15 characters',
        },
      ],
    },
    {
      key: 'lastName',
      label: 'Last Name',
      validators: [
        {
          validate: 'require_without_all',
          fields: ['firstName', 'companyName'],
          errorMessage: 'First Name OR Last Name OR Company Name is required',
        },
        {
          validate: 'length',
          min: 1,
          max: 20,
          errorMessage: 'Last Name cannot exceed 20 characters',
        },
      ],
    },
    {
      key: 'companyName',
      label: 'Company',
      validators: [
        {
          validate: 'require_without_all',
          fields: ['firstName', 'lastName'],
          errorMessage: 'First Name OR Last Name OR Company Name is required',
        },
        {
          validate: 'length',
          min: 1,
          max: 35,
          errorMessage: 'Company Name cannot exceed 35 characters',
        },
      ],
    },

    { key: 'street1', label: 'Street 1' },
    { key: 'street2', label: 'Street 2' },
    { key: 'city', label: 'City' },

    { key: 'postalCode', label: 'Postal Code' },

    {
      key: 'phoneNumber',
      label: 'Phone',
      validators: [
        {
          validate: 'require_without',
          fields: ['emailAddress'],
          errorMessage: 'Email Address OR Phone Number is required',
        },
      ],
    },
    {
      key: 'emailAddress',
      label: 'Email Address',
      type: 'email',
      validators: [
        {
          validate: 'require_without',
          fields: ['phoneNumber'],
          errorMessage: 'Email Address OR Phone Number is required',
        },
        {
          validate: 'length',
          max: 200,
          errorMessage: 'Email Address Name cannot exceed 200 characters.',
        },
      ],
    },
    {
      key: 'phoneExt',
      label: 'Phone Extension',
      validators: [
        {
          validate: 'regex_match',
          regex: '^\\d{1,4}$',
          errorMessage: 'Must be a numeric value and less than 5 digits.',
        },
      ],
    },
    {
      key: 'altPhoneNumber',
      label: 'Alt Phone',
    },
    {
      key: 'altPhoneExt',
      label: 'Alt Phone Extension',
      validators: [
        {
          validate: 'regex_match',
          regex: '^\\d{1,4}$',
          errorMessage: 'Must be a numeric value and less than 5 digits.',
        },
      ],
    },
    {
      key: 'note',
      label: 'Note',
      validators: [
        {
          validate: 'length',
          max: 5000,
          errorMessage: 'Note cannot exceed 5000 characters.',
        },
      ],
    },
    {
      key: 'nextActionDate',
      label: 'Next Action Date',
      type: [
        'date',
        {
          displayFormat: `${dateFormat}`,
        },
      ],
    },

    // service fields
    {
      key: 'recurringPrice',
      label: 'Recurring Price',
      type: ['number', 'decimal_2'],
    },

    { key: 'initialDiscount', label: 'Initial Discount' },
    { key: 'recurringDiscount', label: 'Recurring Discount' },

    {
      key: 'referralValue',
      label: 'Referral Value',
      validators: [
        {
          validate: 'require_with',
          fields: ['referralType'],
          errorMessage: 'Required',
        },
        {
          validate: 'length',
          max: 50,
          errorMessage: 'Referral Value cannot exceed 50 characters.',
        },
      ],
    },

    // other select fields
    createSelectField('stateAbbreviation', 'State/Province', statesProvinces),
    createSelectField('source', 'Source', leadSources),
    createSelectField('campaign', 'Campaign', campaigns),
    createSelectField('targetPest', 'Target Pests', targetPests),
    createSelectField('salesTeamName', 'Sales Team', salesTeams, [
      { validate: 'required' },
    ]),
    createSelectField('branch', 'Branch', branches, [{ validate: 'required' }]),
    createSelectField('salesFunnelName', 'Sales Funnel', salesFunnels, [
      { validate: 'required' },
    ]),
    createSelectField('locationType', 'Location Type', locationTypes),

    createSelectField('division', 'Division', divisions),

    createSelectField('bestContactTime', 'Best Contact Times', contactTimes),
    createSelectField(
      'bestContactMethod',
      'Best Contact Method',
      contactMethods,
    ),
    createSelectField('referralType', 'Referral Type', [
      { label: '', value: '' },
      { label: 'Other', value: 'Other' },
    ]),

    // hidden fields
    { key: 'createdBy', label: 'Created by', hidden: true },
    { key: 'submittedBy', label: 'Submitted by', hidden: true },
    { key: 'type', label: 'Type', hidden: true },
    { key: 'branchId', label: 'Branch Id', hidden: true },
    { key: 'salesTeamId', label: 'Sales Team Id', hidden: true },
    { key: 'salesFunnelId', label: 'Sales Funnel Id', hidden: true },
    { key: 'sourceId', label: 'Source Id', hidden: true },
    { key: 'campaignId', label: 'Campaign Id', hidden: true },
    { key: 'locationTypeId', label: 'Location Type Id', hidden: true },
    { key: 'targetPestId', label: 'Target Pest Id', hidden: true },
    { key: 'createdById', label: 'Created by Id', hidden: true },
    { key: 'divisionId', label: 'Division Id', hidden: true },
  ]
}
